import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { TOUR_EVENT, TOUR_PARAM, TOUR_STEP_PARAM } from "../constants";
import { useTour } from "../useTour";
import { useTourState } from "../useTourState";
import { TourWelcome } from "./TourWelcome";

type TourEntryProps = {
  visitKey: string;
};

export function TourEntry({ visitKey }: TourEntryProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { definition, start, lastVisitedKey } = useTour();
  const { showWelcome, resumeStep, showHelpBeacon, record } = useTourState(definition);
  const deepLinkedKey = useRef<string | null>(null);
  const [isWelcomeOpen, setIsWelcomeOpen] = useState(false);

  const hasDeepLink = searchParams.get(TOUR_PARAM) === definition.id;
  const deepLinkStep = Number(searchParams.get(TOUR_STEP_PARAM));

  useEffect(() => {
    if (!hasDeepLink) return;
    deepLinkedKey.current = visitKey;
    start(Number.isInteger(deepLinkStep) && deepLinkStep > 0 ? deepLinkStep : undefined);
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.delete(TOUR_PARAM);
        next.delete(TOUR_STEP_PARAM);
        return next;
      },
      { replace: true },
    );
  }, [hasDeepLink, deepLinkStep, visitKey, start, setSearchParams]);

  useEffect(() => {
    if (lastVisitedKey.current === visitKey) return;
    lastVisitedKey.current = visitKey;

    if (deepLinkedKey.current !== visitKey) {
      if (showWelcome) setIsWelcomeOpen(true);
      else if (resumeStep !== null) start(resumeStep);
    }
    if (showHelpBeacon) record({ event: TOUR_EVENT.HELP_BEACON_SEEN });
  }, [visitKey, showWelcome, resumeStep, showHelpBeacon, lastVisitedKey, start, record]);

  const handleWelcomeOpenChange = (open: boolean) => {
    if (open) return;
    setIsWelcomeOpen(false);
    record({ event: TOUR_EVENT.SNOOZED });
  };

  const handleWelcomeStart = () => {
    setIsWelcomeOpen(false);
    start();
  };

  return (
    <TourWelcome
      definition={definition}
      open={isWelcomeOpen}
      onOpenChange={handleWelcomeOpenChange}
      onStart={handleWelcomeStart}
    />
  );
}
